import Button from "@mui/material/Button";
import Modal from "../../shared/Modal/Modal";
import { Form, Formik } from "formik";
import { useMemo, useRef, useState } from "react";
import * as Yup from "yup";
import { errorMessages } from "../../../constants/errorMessages";
import Input from "../../shared/Input/Input";
import { ReactComponent as ResendIcon } from "../../../assets/svg/Resend.svg";
import Dropdown from "../../shared/Dropdown/Dropdown";
import "./ResendForm.scss";
import { communicationLogs_LABELS } from "../../../constants/labels/CommunicationLogs";
import { useLazyResendCommunicationQuery } from "../../../services/rtkQueryServices/communicationLogsService";
import { useAuth } from "../../../context/AuthProvider";
import { Utility } from "../../../utils/Utility";
import { LOGIN_LITERALS } from "../../../constants/literals";
import { regex } from "../../../constants/regex";
import { USER_TYPE } from "../../../constants/enums/userTypes";

interface resendRequest {
  notificationDataId: number;
  processId: string;
  initiatedBy: string;
  newRecipientAddress: string;
  newCommunicationType: number;
}
export default function ResendForm(props: any) {
  const [errorMessage, setErrorMessage] = useState<string>("");
  const initialFormValue = useMemo<any>(() => {
    return {
      communicationType: props?.logInformation?.type,
      recipientAddress: "",
    };
  }, [props]);
  const formRef = useRef<any>(null);
  const [isFormValid, setIsFormValid] = useState<boolean>(false);
  const [isFormDirty, setIsFormDirty] = useState<boolean>(false);
  const validationSchema = Yup.object().shape({
    communicationType: Yup.string().required(
      errorMessages.required("Communication Type")
    ),
    recipientAddress: Yup.string().test(
      "recipient-address-validation",
      "Invalid recipient address",
      function (value) {
        var { communicationType } = this.parent;
        if (communicationType === "1") {
          // Email validation
          const emailSchema = Yup.string()
            .required(errorMessages.required("Email Address"))
            .matches(regex.emailValidation, () => errorMessages.invalidEmail)
            .max(128, () => errorMessages.characterLimit("Email address", 128));

          try {
            emailSchema.validateSync(value);
          } catch (err) {
            return this.createError({
              message: () =>
                (err as any)?.message || errorMessages.invalidEmail,
            });
          }
        } else if (communicationType === "3") {
          // Fax validation
          const faxSchema = Yup.string()
            .required(errorMessages.required("Fax number"))
            .matches(
              regex.numberandSpecialCharsValidation,
              () => errorMessages.invalidFaxNumber
            )
            .min(6, () => errorMessages.faxValidation)
            .max(30, () => errorMessages.characterLimit("Fax", 30));

          try {
            faxSchema.validateSync(value);
          } catch (err) {
            return this.createError({
              message: () => (err as any)?.message || errorMessages.invalidFax,
            });
          }
        }

        return true; // If communicationType is not recognized, consider valid
      }
    ),
  });
  const handleOnChange = () => {
    setErrorMessage("");
  };
  const communicationTypes = useMemo(() => {
    if (props.logInformation?.userType === 1) {
      return [{ value: 1, displayValue: "Email" }];
    } else {
      return [
        { value: 1, displayValue: "Email" },
        { value: 3, displayValue: "Fax" },
      ];
    }
  }, [props.logInformation?.userType]);
  const auth = useAuth();
  const [resendCommunication] = useLazyResendCommunicationQuery();
  return (
    <>
      <Modal
        title="Resend "
        headerLogo={<ResendIcon />}
        modalHeaderStyle={{ fontSize: "2rem" }}
        modalWidth="480px"
        isOpen={props.isOpen}
        children={
          <div>
            <div className="m-5 d-flex flex-column gap-3 pb-5 resend-log-details-wrapper">
              <div className="d-flex">
                <div className="log-details-label">Clinic Name: </div>
                <div className="log-details-text">
                  {props.logInformation?.clinicName}
                </div>
              </div>
              <div className="d-flex">
                <div className="log-details-label">BP Site ID: </div>
                <div className="log-details-text">
                  {props.logInformation?.bpSiteId}
                </div>
              </div>
              <div className="d-flex">
                <div className="log-details-label">
                  {communicationLogs_LABELS.recipientType}:{" "}
                </div>
                <div className="log-details-text">
                  {USER_TYPE[props?.logInformation?.userType]}
                </div>
              </div>
              <div className="d-flex">
                <div className="log-details-label">
                  {communicationLogs_LABELS.recipientName}:{" "}
                </div>
                <div className="log-details-text">
                  {props.logInformation?.recipientName}
                </div>
              </div>
              <div className="d-flex">
                <div className="log-details-label">
                  {communicationLogs_LABELS.recipientAddress}:{" "}
                </div>
                <div className="log-details-text">
                  {props.logInformation?.recipientAddress}
                </div>
              </div>
              <div className="d-flex">
                <div className="log-details-label">Last Initiated: </div>
                <div className="log-details-text">
                  {Utility.ConvertToDateTime(props.logInformation?.createdAt)}
                </div>
              </div>
            </div>
            <Formik
              innerRef={formRef}
              initialValues={initialFormValue}
              validationSchema={validationSchema}
              onSubmit={(values) => {
                var request: resendRequest = {
                  notificationDataId: props.logInformation?.notificationId,
                  processId: props?.logInformation?.processId,
                  newCommunicationType: values.communicationType,
                  newRecipientAddress: values.recipientAddress,
                  initiatedBy: auth?.authClaims?.userName || "",
                };
                resendCommunication(request).then((res) => {
                  if (res.isSuccess && !res.isLoading) {
                    props?.onClose();
                    props?.onReload();
                  } else if (res.isError) {
                    setErrorMessage(
                      (res.error as any).data.message ||
                        LOGIN_LITERALS.somethingWentWrong
                    );
                  }
                });
              }}
            >
              {(formik) => {
                setIsFormValid(formik?.isValid);
                setIsFormDirty(formik?.dirty);
                return (
                  <Form
                    onSubmit={formik.handleSubmit}
                    className="resend-form-container"
                  >
                    <div className="resend-form d-flex flex-column p-5 pt-0 d-flex flex-column gap-3">
                      <Dropdown
                        menuItems={communicationTypes}
                        placeholder={"Communication Type"}
                        label={"Communication Type"}
                        name={"communicationType"}
                        selectedValue={formik.values?.communicationType}
                        onChange={(value: any) => {
                          formik.setFieldValue("communicationType", value);
                        }}
                        isFieldTouched={() => {
                          formik.setFieldTouched("communicationType");
                        }}
                        onInputChange={(
                          event: any,
                          newInputValue: string
                        ) => {}}
                        dropdownSearchInputPlaceHolder={"type"}
                        errorMsg={
                          formik.touched.communicationType &&
                          formik.errors.communicationType &&
                          (formik.errors as any).communicationType
                        }
                      />
                      <Input
                        name="recipientAddress"
                        label={
                          formik.values?.communicationType === 3
                            ? "Fax Number"
                            : "Email Address"
                        }
                        placeholder={
                          formik.values?.communicationType === 3
                            ? "+61X XXXX XXXX"
                            : "name@domain.com"
                        }
                        disabled={!formik.values.communicationType}
                        showHelpTextIcon
                        labelHelpText={
                          communicationLogs_LABELS.recipientAddressHelpText
                        }
                        infoIconProps={{
                          width: "14px",
                          height: "14px",
                          fillColor: "#636363",
                        }}
                        errorMsg={
                          formik.touched.recipientAddress &&
                          formik.errors.recipientAddress &&
                          (formik.errors as any).recipientAddress
                        }
                        value={formik.values.recipientAddress}
                        onInput={formik.handleChange}
                        onChange={handleOnChange}
                        required
                      />
                    </div>
                    {errorMessage && (
                      <div className="error-message px-5">{errorMessage}</div>
                    )}
                  </Form>
                );
              }}
            </Formik>
          </div>
        }
        onClose={() => {
          setErrorMessage("");
          props.onClose();
        }}
        closeOnOverLayClick={false}
        headerBg="var(--primary-color)"
        closeIconColor={"var(--white)"}
        modalFooter={
          <div className="d-flex justify-content-between resend-footer gap-1 px-5 py-3">
            <Button
              className="cancel-button px-5"
              onClick={() => {
                setErrorMessage("");
                props.onClose();
              }}
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              className="submit-button px-5"
              variant="contained"
              disabled={!isFormDirty || !isFormValid}
              onClick={() => {
                formRef.current.handleSubmit();
              }}
            >
              Resend
            </Button>
          </div>
        }
      />
    </>
  );
}
